/**
 * ActionEffectMigrationPage
 *
 * Migrate ObjectType automation triggers (and their nested effects) between two registered
 * streem-backend instances, independent of any checklist.
 *
 * Wizard:
 *   1. Instances   — InstancePicker (source + target)
 *   2. ObjectType  — pick one ObjectType on the source instance
 *   3. Triggers    — multi-select the triggers to migrate
 *   4. Review & Run — POST /api/action-effect/migrate. The backend matches the target ObjectType by
 *      externalId and validates that every RESOURCE_BASED trigger's referenced property/relation
 *      exists on the target. If any is missing the whole batch is rejected and the missing
 *      dependencies are shown so the operator can migrate the ontology first.
 */

import React, { useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Loader2,
  ServerCrash,
  Zap,
} from "lucide-react";

import { AppSidebar } from "../components/app-sidebar";
import { SiteHeader } from "../components/site-header";
import { InstancePicker, InstancePairBadge } from "../components/InstancePicker";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Skeleton } from "../components/ui/skeleton";
import { Separator } from "../components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { SidebarInset, SidebarProvider } from "../components/ui/sidebar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { cn } from "../lib/utils";
import { useToast } from "../lib/use-toast";
import {
  useActionEffectMigrate,
  useActionEffectObjectTypes,
  useActionEffectTriggers,
  useInstanceFacilities,
  useInstances,
  type ActionEffectMigrationResult,
  type ActionEffectTrigger,
  type Instance,
} from "../lib/api-central-mu";
import { useInstancePairStore } from "../store/useInstancePairStore";

type Step = 1 | 2 | 3 | 4;

/** A chosen facility (id + display name), captured for the audit trail. */
type FacilitySel = { id: string; name: string } | null;

export default function ActionEffectMigrationPage() {
  const [step, setStep] = useState<Step>(1);

  const sourceServiceId = useInstancePairStore((s) => s.sourceServiceId);
  const targetServiceId = useInstancePairStore((s) => s.targetServiceId);

  const [objectTypeId, setObjectTypeId] = useState<string | null>(null);
  const [selectedTriggerIds, setSelectedTriggerIds] = useState<Set<string>>(new Set());
  const [sourceFacility, setSourceFacility] = useState<FacilitySel>(null);
  const [targetFacility, setTargetFacility] = useState<FacilitySel>(null);

  const { data: instances = [] } = useInstances();
  const source = instances.find((i) => i.id === sourceServiceId) ?? null;
  const target = instances.find((i) => i.id === targetServiceId) ?? null;

  const canAdvanceFromStep1 =
    !!source && !!target && source.id !== target.id && !!sourceFacility && !!targetFacility;
  const canAdvanceFromStep2 = !!objectTypeId;
  const canAdvanceFromStep3 = selectedTriggerIds.size > 0;

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <SiteHeader />
        <main className="p-6 lg:p-8 space-y-6">
          <PageHeader />

          <Stepper step={step} />

          {step === 1 && (
            <Step1Instances
              onNext={() => setStep(2)}
              canAdvance={canAdvanceFromStep1}
              sourceServiceId={sourceServiceId}
              targetServiceId={targetServiceId}
              sourceFacility={sourceFacility}
              targetFacility={targetFacility}
              setSourceFacility={setSourceFacility}
              setTargetFacility={setTargetFacility}
            />
          )}

          {step === 2 && (
            <Step2ObjectType
              source={source}
              objectTypeId={objectTypeId}
              setObjectTypeId={(id) => {
                setObjectTypeId(id);
                setSelectedTriggerIds(new Set());
              }}
              onBack={() => setStep(1)}
              onNext={() => setStep(3)}
              canAdvance={canAdvanceFromStep2}
            />
          )}

          {step === 3 && (
            <Step3Triggers
              sourceServiceId={sourceServiceId}
              objectTypeId={objectTypeId}
              selectedIds={selectedTriggerIds}
              setSelectedIds={setSelectedTriggerIds}
              onBack={() => setStep(2)}
              onNext={() => setStep(4)}
              canAdvance={canAdvanceFromStep3}
            />
          )}

          {step === 4 && (
            <Step4ReviewAndRun
              source={source}
              target={target}
              objectTypeId={objectTypeId}
              triggerIds={Array.from(selectedTriggerIds)}
              sourceFacility={sourceFacility}
              targetFacility={targetFacility}
              onBack={() => setStep(3)}
              onStartOver={() => {
                setStep(1);
                setObjectTypeId(null);
                setSelectedTriggerIds(new Set());
                setSourceFacility(null);
                setTargetFacility(null);
              }}
            />
          )}
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}

// =====================================================================================
// Header + stepper
// =====================================================================================

function PageHeader() {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="flex items-center gap-2">
          <Zap className="size-5 text-primary" />
          <h1 className="text-2xl font-semibold tracking-tight">Action Effect Migration</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Copy an ObjectType's automation triggers (and their effects) between two registered Leucine
          instances. The target ObjectType is matched by external ID; any property or relation a
          trigger depends on must already exist on the target.
        </p>
      </div>
    </div>
  );
}

const STEP_LABELS = ["Instances", "ObjectType", "Triggers", "Review"] as const;

function Stepper({ step }: { step: Step }) {
  return (
    <ol className="flex items-center gap-2 text-sm">
      {STEP_LABELS.map((label, idx) => {
        const i = (idx + 1) as Step;
        const done = i < step;
        const active = i === step;
        return (
          <li key={label} className="flex items-center gap-2">
            <div
              className={cn(
                "flex size-6 items-center justify-center rounded-full text-xs font-medium",
                done && "bg-primary text-primary-foreground",
                active && "bg-primary/15 text-primary border border-primary",
                !done && !active && "bg-muted text-muted-foreground"
              )}
            >
              {done ? <CheckCircle2 className="size-4" /> : i}
            </div>
            <span className={cn("font-medium", !active && "text-muted-foreground")}>{label}</span>
            {idx < STEP_LABELS.length - 1 && (
              <ChevronRight className="size-4 text-muted-foreground mx-1" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

// =====================================================================================
// Step 1 — Instances
// =====================================================================================

function Step1Instances({
  onNext,
  canAdvance,
  sourceServiceId,
  targetServiceId,
  sourceFacility,
  targetFacility,
  setSourceFacility,
  setTargetFacility,
}: {
  onNext: () => void;
  canAdvance: boolean;
  sourceServiceId: number | null;
  targetServiceId: number | null;
  sourceFacility: FacilitySel;
  targetFacility: FacilitySel;
  setSourceFacility: (f: FacilitySel) => void;
  setTargetFacility: (f: FacilitySel) => void;
}) {
  return (
    <div className="space-y-4">
      {/* Pair is chosen (and permission-checked) on the select-instances step — locked here. */}
      <InstancePicker disabled />
      <Card>
        <CardHeader>
          <CardTitle>Facilities</CardTitle>
          <CardDescription>
            Choose the source and target facility. These are recorded on the migration log.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <FacilitySelect
            label="Source facility"
            serviceId={sourceServiceId}
            value={sourceFacility}
            onChange={setSourceFacility}
          />
          <FacilitySelect
            label="Target facility"
            serviceId={targetServiceId}
            value={targetFacility}
            onChange={setTargetFacility}
          />
        </CardContent>
      </Card>
      <div className="flex justify-end">
        <Button onClick={onNext} disabled={!canAdvance}>
          Next
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
}

function FacilitySelect({
  label,
  serviceId,
  value,
  onChange,
}: {
  label: string;
  serviceId: number | null;
  value: FacilitySel;
  onChange: (f: FacilitySel) => void;
}) {
  const { data: facilities = [], isLoading } = useInstanceFacilities(serviceId ?? undefined);
  return (
    <div className="grid w-full items-center gap-1.5">
      <span className="text-sm font-medium">{label}</span>
      <Select
        value={value?.id ?? ""}
        onValueChange={(id) => {
          const f = facilities.find((x) => x.id === id);
          onChange(f ? { id: f.id, name: f.name } : null);
        }}
        disabled={!serviceId || isLoading}
      >
        <SelectTrigger>
          <SelectValue placeholder={!serviceId ? "Pick an instance first" : "Select facility"} />
        </SelectTrigger>
        <SelectContent>
          {facilities.length === 0 ? (
            <SelectItem value="none" disabled>
              {isLoading ? "Loading facilities…" : "No facilities available"}
            </SelectItem>
          ) : (
            facilities.map((f) => (
              <SelectItem key={f.id} value={f.id}>
                {f.name}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
    </div>
  );
}

// =====================================================================================
// Step 2 — ObjectType
// =====================================================================================

function Step2ObjectType({
  source,
  objectTypeId,
  setObjectTypeId,
  onBack,
  onNext,
  canAdvance,
}: {
  source: Instance | null;
  objectTypeId: string | null;
  setObjectTypeId: (id: string | null) => void;
  onBack: () => void;
  onNext: () => void;
  canAdvance: boolean;
}) {
  const { data: objectTypes = [], isLoading, error } = useActionEffectObjectTypes(source?.id);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selectedObjectType = objectTypes.find((ot) => ot.id === objectTypeId);
  const filteredObjectTypes = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return objectTypes;
    return objectTypes.filter((ot) =>
      `${ot.displayName ?? ""} ${ot.externalId ?? ""}`.toLowerCase().includes(q),
    );
  }, [objectTypes, query]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Choose an ObjectType
            {source ? (
              <Badge variant="outline" className="font-normal">
                {source.name}
              </Badge>
            ) : null}
          </CardTitle>
          <CardDescription>Pick the source ObjectType whose triggers you want to migrate.</CardDescription>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-sm text-destructive flex items-center gap-2">
              <ServerCrash className="size-4" /> Failed to load object types
            </div>
          ) : isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          ) : objectTypes.length === 0 ? (
            <p className="text-sm text-muted-foreground">No object types found on this instance.</p>
          ) : (
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={pickerOpen}
                  className="w-full justify-between font-normal"
                >
                  {selectedObjectType ? (
                    <span className="truncate">
                      {selectedObjectType.displayName}{" "}
                      <span className="text-muted-foreground">({selectedObjectType.externalId})</span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Choose an object type…</span>
                  )}
                  <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="p-0"
                align="start"
                style={{ width: "var(--radix-popover-trigger-width)" }}
              >
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Search by name or external id…"
                    value={query}
                    onValueChange={setQuery}
                  />
                  <CommandList>
                    <CommandEmpty>No object types match "{query}".</CommandEmpty>
                    <CommandGroup>
                      {filteredObjectTypes.map((ot) => (
                        <CommandItem
                          key={ot.id}
                          value={ot.id}
                          onSelect={() => {
                            setObjectTypeId(ot.id === objectTypeId ? null : ot.id);
                            setPickerOpen(false);
                          }}
                        >
                          <span className="truncate">
                            {ot.displayName}{" "}
                            <span className="text-muted-foreground">({ot.externalId})</span>
                          </span>
                          <Check
                            className={cn(
                              "ml-auto size-4",
                              objectTypeId === ot.id ? "opacity-100" : "opacity-0",
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          <ChevronLeft className="size-4" />
          Back
        </Button>
        <Button onClick={onNext} disabled={!canAdvance}>
          Next
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
}

// =====================================================================================
// Step 3 — Triggers
// =====================================================================================

/** Human-readable summary of what a trigger fires on. */
function triggerCondition(trigger: ActionEffectTrigger): string {
  if (trigger.triggerType === "RESOURCE_BASED") {
    const condition = trigger.resourceConfig?.updateCondition;
    const event = trigger.resourceConfig?.eventType;
    if (condition?.targetExternalId) {
      const type = (condition.targetType ?? "PROPERTY").toLowerCase();
      return `${event ?? "UPDATE"} · ${type} "${condition.targetExternalId}"`;
    }
    return event ?? "RESOURCE_BASED";
  }
  return trigger.triggerType ?? "—";
}

function Step3Triggers({
  sourceServiceId,
  objectTypeId,
  selectedIds,
  setSelectedIds,
  onBack,
  onNext,
  canAdvance,
}: {
  sourceServiceId: number | null;
  objectTypeId: string | null;
  selectedIds: Set<string>;
  setSelectedIds: (s: Set<string>) => void;
  onBack: () => void;
  onNext: () => void;
  canAdvance: boolean;
}) {
  const { data: triggers = [], isLoading, error } = useActionEffectTriggers(sourceServiceId, objectTypeId);

  const allSelected = triggers.length > 0 && triggers.every((t) => selectedIds.has(t._id));

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(triggers.map((t) => t._id)) : new Set());
  };

  const toggleOne = (id: string, checked: boolean) => {
    const next = new Set(selectedIds);
    if (checked) next.add(id);
    else next.delete(id);
    setSelectedIds(next);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Select triggers</CardTitle>
          <CardDescription>Choose the triggers (with their effects) to migrate to the target.</CardDescription>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-sm text-destructive flex items-center gap-2">
              <ServerCrash className="size-4" /> Failed to load triggers
            </div>
          ) : isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          ) : triggers.length === 0 ? (
            <p className="text-sm text-muted-foreground">This object type has no triggers.</p>
          ) : (
            <>
              <div className="mb-2 text-sm text-muted-foreground">
                {selectedIds.size} of {triggers.length} selected
              </div>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={allSelected}
                          onCheckedChange={(c) => toggleAll(Boolean(c))}
                          aria-label="Select all"
                        />
                      </TableHead>
                      <TableHead>Trigger</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Fires on</TableHead>
                      <TableHead className="text-right">Effects</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {triggers.map((t) => (
                      <TableRow key={t._id} className={cn(selectedIds.has(t._id) && "bg-primary/5")}>
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(t._id)}
                            onCheckedChange={(c) => toggleOne(t._id, Boolean(c))}
                            aria-label={`Select ${t.triggerName ?? t._id}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{t.triggerName ?? "(unnamed)"}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-normal">
                            {t.triggerType ?? "—"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{triggerCondition(t)}</TableCell>
                        <TableCell className="text-right tabular-nums">{t.effects?.length ?? 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          <ChevronLeft className="size-4" />
          Back
        </Button>
        <Button onClick={onNext} disabled={!canAdvance}>
          Review
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
}

// =====================================================================================
// Step 4 — Review & Run
// =====================================================================================

function Step4ReviewAndRun({
  source,
  target,
  objectTypeId,
  triggerIds,
  sourceFacility,
  targetFacility,
  onBack,
  onStartOver,
}: {
  source: Instance | null;
  target: Instance | null;
  objectTypeId: string | null;
  triggerIds: string[];
  sourceFacility: FacilitySel;
  targetFacility: FacilitySel;
  onBack: () => void;
  onStartOver: () => void;
}) {
  const { toast } = useToast();
  const { mutate, isPending, data: result, reset } = useActionEffectMigrate();

  // Resolve the selected ObjectType so the review shows its display name (not the raw Mongo id).
  // Served from the TanStack Query cache populated in Step 2 — no extra fetch in practice.
  const { data: objectTypes = [] } = useActionEffectObjectTypes(source?.id);
  const selectedObjectType = objectTypes.find((ot) => ot.id === objectTypeId) ?? null;

  const runMigration = (autoMigrateObjectType: boolean) => {
    if (!source || !target || !objectTypeId || triggerIds.length === 0) {
      toast({
        variant: "destructive",
        title: "Missing input",
        description: "Pick source, target, an object type, and at least one trigger.",
      });
      return;
    }
    mutate(
      {
        sourceServiceId: source.id,
        targetServiceId: target.id,
        sourceObjectTypeId: objectTypeId,
        triggerIds,
        sourceFacilityId: sourceFacility?.id,
        sourceFacilityName: sourceFacility?.name,
        targetFacilityId: targetFacility?.id,
        targetFacilityName: targetFacility?.name,
        autoMigrateObjectType,
      },
      {
        onSuccess: (res) => {
          if (res.errors.length === 0) {
            toast({
              title: "Migration complete",
              description: `${res.triggersMigrated} migrated, ${res.triggersSkipped} already present${
                res.objectTypeMigrated ? " (ObjectType migrated first)" : ""
              }.`,
            });
          }
        },
      }
    );
  };

  const handleRun = () => runMigration(false);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Migration plan</CardTitle>
          <CardDescription>Confirm what will run, then click Run migration.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <InstancePairBadge sourceName={source?.name} targetName={target?.name} />
          </div>
          <Separator />
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <dt className="text-muted-foreground">Source ObjectType</dt>
            <dd className="break-all">
              {selectedObjectType ? (
                <>
                  {selectedObjectType.displayName}{" "}
                  <span className="font-mono text-xs text-muted-foreground">({selectedObjectType.externalId})</span>
                </>
              ) : (
                <span className="font-mono">{objectTypeId ?? "—"}</span>
              )}
            </dd>
            <dt className="text-muted-foreground">Triggers selected</dt>
            <dd>{triggerIds.length}</dd>
          </dl>
        </CardContent>
        <CardFooter className="justify-between">
          <Button variant="outline" onClick={onBack} disabled={isPending}>
            <ChevronLeft className="size-4" />
            Back
          </Button>
          <Button onClick={handleRun} disabled={isPending || (!!result && result.errors.length === 0)}>
            {isPending ? <Loader2 className="size-4 animate-spin" /> : <ArrowRight className="size-4" />}
            {isPending ? "Running…" : result && result.errors.length > 0 ? "Retry" : "Run migration"}
          </Button>
        </CardFooter>
      </Card>

      {result ? (
        <ResultPanel
          result={result}
          isPending={isPending}
          onRemediate={() => runMigration(true)}
          onStartOver={() => {
            reset();
            onStartOver();
          }}
        />
      ) : null}
    </div>
  );
}

function ResultPanel({
  result,
  isPending,
  onRemediate,
  onStartOver,
}: {
  result: ActionEffectMigrationResult;
  isPending: boolean;
  onRemediate: () => void;
  onStartOver: () => void;
}) {
  const blocked = result.errors.length > 0;

  if (blocked) {
    // If we already attempted the ObjectType migration and it still didn't resolve, remediation
    // can't help (e.g. the source ObjectType itself lacks the referenced property) — hide the button.
    const canRemediate = !result.objectTypeMigrated;
    return (
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="size-5" />
            Missing on target — ObjectType not ready
          </CardTitle>
          <CardDescription>
            Nothing was migrated. The target is missing the ObjectType
            {result.objectTypeExternalId ? ` "${result.objectTypeExternalId}"` : ""} or some of the
            properties/relations these triggers depend on.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
            <ul className="text-sm list-disc pl-5 space-y-1">
              {result.errors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
          {canRemediate ? (
            <p className="text-sm text-muted-foreground">
              Migrate the ObjectType (with its properties and relations) to the target first, then the
              selected triggers — in one step.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              The ObjectType was migrated, but a dependency is still missing — it likely doesn't exist on
              the source ObjectType either. Fix the source definition, then retry.
            </p>
          )}
        </CardContent>
        <CardFooter className="justify-between">
          <Button variant="outline" onClick={onStartOver} disabled={isPending}>
            Start over
          </Button>
          {canRemediate ? (
            <Button onClick={onRemediate} disabled={isPending}>
              {isPending ? <Loader2 className="size-4 animate-spin" /> : <ArrowRight className="size-4" />}
              {isPending ? "Migrating…" : "Migrate ObjectType, then triggers"}
            </Button>
          ) : null}
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle2 className="size-5 text-emerald-600" />
          Migration complete
        </CardTitle>
        <CardDescription>
          ObjectType "{result.objectTypeExternalId}" — triggers pushed to the target.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {result.objectTypeMigrated ? (
          <div className="rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 p-3 text-sm text-emerald-900 dark:text-emerald-100">
            The ObjectType (with its properties and relations) was migrated to the target first, then the
            triggers.
          </div>
        ) : null}
        <dl className="grid grid-cols-2 gap-2 text-sm">
          <Metric label="Triggers requested" value={result.triggersRequested} />
          <Metric label="Triggers migrated" value={result.triggersMigrated} />
          <Metric label="Already on target (skipped)" value={result.triggersSkipped} />
        </dl>
      </CardContent>
      <CardFooter>
        <Button variant="outline" onClick={onStartOver}>
          Start another migration
        </Button>
      </CardFooter>
    </Card>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-mono font-medium">{value}</dd>
    </>
  );
}
